import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Download, ShieldCheck, ShieldOff } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useDocuments } from '@/lib/documents';
import type { Program } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

interface Approval {
  id: string;
  tiersApproved: number[];
  approvedByName: string;
  approvedAt: string;
  provider: string;
  clientRolesAllowed: boolean;
  letter: { id: string; title: string } | null;
  revokedAt: string | null;
  revokedNote: string | null;
}
interface AiState {
  model: string;
  configured: boolean;
  capUsd: string;
  hourlyLimit: number;
  monthSpendUsd: string;
  approvals: Approval[];
}
interface Run {
  id: string;
  at: string;
  user: string;
  purpose: string;
  tier: number;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: string;
  contextRecordIds: Array<{ type: string; id: string }>;
  documentVersionIds: string[];
}

/** What a run read, by record type and id, with document versions last. */
const contextOf = (r: Run) => [
  ...r.contextRecordIds.map((c) => `${c.type.replace(/([a-z])([A-Z])/g, '$1 $2')} ${c.id}`),
  ...r.documentVersionIds.map((id) => `Document version ${id}`),
];

const TIER = {
  1: 'Tier 1: program data typed into HELM',
  2: 'Tier 2: BlackWolf-authored documents',
  3: `Tier 3: client-provided documents`,
} as const;

export function AiTab({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin/ai`;
  const qc = useQueryClient();
  const toast = useToast();
  const docs = useDocuments(program.id);
  const q = useQuery({ queryKey: ['admin', program.id, 'ai'], queryFn: () => api.get<AiState>(base) });
  const runs = useQuery({ queryKey: ['admin', program.id, 'ai-runs'], queryFn: () => api.get<Run[]>(`${base}/runs`) });
  const [recording, setRecording] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [limits, setLimits] = useState<{ cap: string; hourly: string } | null>(null);
  const [f, setF] = useState({
    tiers: [1] as number[],
    approvedByName: '',
    approvedAt: '',
    provider:
      'Anthropic API (Claude), commercial terms: no training on inputs or outputs; zero data retention where applied',
    clientRolesAllowed: false,
    letterDocumentId: '',
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', program.id] });
    // Assistant controls everywhere read this; an approval or revocation changes them at once.
    void qc.invalidateQueries({ queryKey: ['ai', program.id] });
  };
  const record = useMutation({
    mutationFn: () =>
      api.post(`${base}/approvals`, {
        tiersApproved: f.tiers,
        approvedByName: f.approvedByName,
        approvedAt: f.approvedAt,
        provider: f.provider,
        clientRolesAllowed: f.clientRolesAllowed,
        letterDocumentId: f.letterDocumentId,
      }),
    onSuccess: () => {
      toast.ok('Approval recorded. The assistant is available for the approved tiers.');
      setRecording(false);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const revoke = useMutation({
    mutationFn: () => api.post(`${base}/approvals/${revoking}/revoke`, { note }),
    onSuccess: () => {
      toast.ok('Approval revoked. Every assistant control is off.');
      setRevoking(null);
      setNote('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const saveLimits = useMutation({
    mutationFn: () =>
      api.patch(`${base}/settings`, {
        aiMonthlyCapUsd: Number(limits!.cap),
        aiUserHourlyLimit: Number(limits!.hourly),
      }),
    onSuccess: () => {
      setLimits(null);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!q.data) return <Spinner className="h-48" />;
  const s = q.data;
  const active = s.approvals.find((a) => !a.revokedAt);

  return (
    <div className="space-y-5">
      {active ? (
        <Alert tone="ok" title={`Approved by ${active.approvedByName} on ${fmtDate(active.approvedAt)}`}>
          {active.tiersApproved.map((t) => TIER[t as 1 | 2 | 3]).join('; ')}.{' '}
          {active.clientRolesAllowed ? 'Client roles may use it.' : 'BlackWolf only.'}
        </Alert>
      ) : (
        <Alert tone="warn" title={`Awaiting ${program.clientShortName} approval`}>
          Agreement §9 bars client information from any AI system without {program.clientShortName}'s prior written
          approval. Every assistant control shows this message and no call is made until the signed letter is recorded
          here.
        </Alert>
      )}

      <Card className="grid gap-4 p-5 sm:grid-cols-4">
        <div>
          <p className="text-xs text-muted">Model (pinned)</p>
          <p className="font-mono text-sm">{s.model}</p>
        </div>
        <div>
          <p className="text-xs text-muted">Server key</p>
          <p className="text-sm">{s.configured ? 'Configured' : 'Not configured'}</p>
        </div>
        <div>
          <p className="text-xs text-muted">This month</p>
          <p className="text-sm">
            ${s.monthSpendUsd} of ${s.capUsd} cap
          </p>
        </div>
        <div>
          <p className="text-xs text-muted">Per person</p>
          <p className="text-sm">{s.hourlyLimit} runs an hour</p>
        </div>
        <div className="flex gap-2 sm:col-span-4">
          {!active && (
            <Button onClick={() => setRecording(true)}>
              <ShieldCheck /> Record the signed approval
            </Button>
          )}
          {active && (
            <Button variant="danger" onClick={() => setRevoking(active.id)}>
              <ShieldOff /> Revoke
            </Button>
          )}
          <Button variant="secondary" onClick={() => setLimits({ cap: s.capUsd, hourly: String(s.hourlyLimit) })}>
            Change limits
          </Button>
        </div>
      </Card>

      <Card className="p-0">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Run log</h2>
          <a
            href={`${base}/runs.csv`}
            className="ml-auto inline-flex items-center gap-1.5 text-[13px] text-brand-text hover:underline"
          >
            <Download className="size-4" /> CSV for {program.clientShortName}
          </a>
        </div>
        {!runs.data?.length ? (
          <p className="flex items-center gap-2 px-4 py-4 text-sm text-muted">
            <Bot className="size-4" /> No assistant runs.
          </p>
        ) : (
          <table className="w-full text-[13px]">
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-muted">{fmtDateTime(r.at)}</td>
                  <td className="px-2 py-2">{r.user}</td>
                  <td className="px-2 py-2">
                    {r.purpose.replace(/_/g, ' ')}
                    <details className="text-xs text-muted">
                      <summary className="cursor-pointer">
                        Read {r.contextRecordIds.length} record{r.contextRecordIds.length === 1 ? '' : 's'}
                        {r.documentVersionIds.length
                          ? `, ${r.documentVersionIds.length} document version${r.documentVersionIds.length === 1 ? '' : 's'}`
                          : ''}
                      </summary>
                      <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
                        {contextOf(r).map((x) => (
                          <li key={x}>{x}</li>
                        ))}
                      </ul>
                    </details>
                  </td>
                  <td className="px-2 py-2">
                    <Badge>Tier {r.tier}</Badge>
                  </td>
                  <td className="px-2 py-2 text-muted tabular-nums">
                    {r.inputTokens} in, {r.outputTokens} out
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">${r.costUsd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {s.approvals.length > 0 && (
        <Card className="p-0">
          <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Approval history</h2>
          <ul className="divide-y divide-border text-[13px]">
            {s.approvals.map((a) => (
              <li key={a.id} className="px-4 py-2.5">
                <span className="font-medium">{a.approvedByName}</span>
                <span className="text-muted">
                  {' '}
                  · {fmtDate(a.approvedAt)} · tiers {a.tiersApproved.join(', ')} · {a.provider}
                </span>
                {a.letter && <span className="text-muted"> · {a.letter.title}</span>}
                {a.revokedAt && (
                  <p className="text-xs text-bad">
                    Revoked {fmtDateTime(a.revokedAt)}: {a.revokedNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog
        open={recording}
        onClose={() => setRecording(false)}
        wide
        title="Record the signed approval"
        description={`File the signed letter in Documents first. The approval names what ${program.clientShortName} approved: the tiers, the provider and its terms.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRecording(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => record.mutate()}
              loading={record.isPending}
              disabled={!f.tiers.length || f.approvedByName.trim().length < 3 || !f.approvedAt || !f.letterDocumentId}
            >
              Record approval
            </Button>
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-[13px] font-medium">Tiers approved</p>
            {([1, 2, 3] as const).map((t) => (
              <Checkbox
                key={t}
                id={`tier-${t}`}
                checked={f.tiers.includes(t)}
                onChange={(on) => setF({ ...f, tiers: on ? [...f.tiers, t].sort() : f.tiers.filter((x) => x !== t) })}
                label={TIER[t]}
              />
            ))}
            <Checkbox
              id="client-roles"
              checked={f.clientRolesAllowed}
              onChange={(on) => setF({ ...f, clientRolesAllowed: on })}
              label={`${program.clientShortName} roles may use the assistant`}
              hint="Leave off unless the letter says so."
            />
          </div>
          <div className="space-y-3">
            <Field label="Signed by" htmlFor="ap-by">
              <Input
                id="ap-by"
                value={f.approvedByName}
                onChange={(e) => setF({ ...f, approvedByName: e.target.value })}
              />
            </Field>
            <Field label="Date signed" htmlFor="ap-on">
              <Input
                id="ap-on"
                type="date"
                value={f.approvedAt}
                onChange={(e) => setF({ ...f, approvedAt: e.target.value })}
              />
            </Field>
            <Field label="Signed letter" htmlFor="ap-letter">
              <Select
                id="ap-letter"
                value={f.letterDocumentId}
                onChange={(e) => setF({ ...f, letterDocumentId: e.target.value })}
              >
                <option value="">Choose the filed letter</option>
                {docs.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Provider and terms approved" htmlFor="ap-provider">
              <Textarea
                id="ap-provider"
                rows={3}
                value={f.provider}
                onChange={(e) => setF({ ...f, provider: e.target.value })}
              />
            </Field>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title="Revoke the approval?"
        description="Every assistant control turns off at once. Past runs stay in the log."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => revoke.mutate()}
              loading={revoke.isPending}
              disabled={note.trim().length < 5}
            >
              Revoke
            </Button>
          </>
        }
      >
        <Field label="Why" htmlFor="rv-note">
          <Textarea id="rv-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </Dialog>

      <Dialog
        open={!!limits}
        onClose={() => setLimits(null)}
        title="Assistant limits"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLimits(null)}>
              Cancel
            </Button>
            <Button onClick={() => saveLimits.mutate()} loading={saveLimits.isPending}>
              Save
            </Button>
          </>
        }
      >
        {limits && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Monthly cap (USD)" htmlFor="lim-cap" hint="At least $1.">
              <Input
                id="lim-cap"
                inputMode="decimal"
                value={limits.cap}
                onChange={(e) => setLimits({ ...limits, cap: e.target.value })}
              />
            </Field>
            <Field label="Runs per person per hour" htmlFor="lim-hr">
              <Input
                id="lim-hr"
                inputMode="numeric"
                value={limits.hourly}
                onChange={(e) => setLimits({ ...limits, hourly: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Dialog>
    </div>
  );
}
